import { formatDuration, formatMoney } from "@pages/profile-v2/utils";
import {
	LIMIT_PERIOD_ORDER,
	LIMIT_TYPES,
	RG_ERROR_CODES,
	SESSION_TIME_MIN_SECONDS,
} from "./constants";

export const getProgressPercent = (currentUsage, effectiveMax) => {
	if (!effectiveMax || effectiveMax <= 0) {
		return 0;
	}

	return Math.min(100, Math.round(((Number(currentUsage) || 0) / effectiveMax) * 100));
};

export const isIncrease = (currentValue, newValue) => {
	const current = Number(currentValue) || 0;
	const next = Number(newValue) || 0;

	if (current <= 0) {
		return false;
	}

	return next > current;
};

export const isDecrease = (currentValue, newValue) => {
	const current = Number(currentValue) || 0;
	const next = Number(newValue) || 0;

	if (current <= 0) {
		return false;
	}

	return next < current;
};

export const clampToBackofficeMax = (value, backofficeMax) => {
	if (backofficeMax == null) {
		return value;
	}

	return Math.min(Number(value) || 0, Number(backofficeMax));
};

export const formatPendingCountdown = (effectiveAt, translate) => {
	if (!effectiveAt) {
		return "";
	}

	const remainingMs = Math.max(0, Number(effectiveAt) - Date.now());
	const totalMinutes = Math.ceil(remainingMs / 60000);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;

	if (hours > 0) {
		return translate("limits.pending.countdownHours", { hours, minutes });
	}

	return translate("limits.pending.countdownMinutes", { minutes: Math.max(minutes, 1) });
};

export const findLimit = (limits, type, period) =>
	(limits || []).find((limit) => limit.type === type && limit.period === period) || null;

export const formatLimitValue = (limit, translate) => {
	if (!limit?.effectiveMax && !limit?.userMax) {
		return translate("limits.noLimitSet");
	}

	const value = limit.effectiveMax ?? limit.userMax;

	if (limit.type === LIMIT_TYPES.SESSION_TIME || limit.unit === "seconds") {
		return formatDuration(value, translate);
	}

	if (limit.unit === "amount") {
		return formatMoney(value, limit.currency);
	}

	return String(value);
};

export const formatUsageValue = (limit, translate) => {
	if (limit.type === LIMIT_TYPES.SESSION_TIME || limit.unit === "seconds") {
		return formatDuration(limit.currentUsage, translate);
	}

	if (limit.unit === "amount") {
		return formatMoney(limit.currentUsage, limit.currency);
	}

	return String(limit.currentUsage ?? 0);
};

export const formatRemainingValue = (limit, translate) => {
	if (limit.remainingAllowance == null) {
		return "—";
	}

	if (limit.type === LIMIT_TYPES.SESSION_TIME || limit.unit === "seconds") {
		return formatDuration(limit.remainingAllowance, translate);
	}

	if (limit.unit === "amount") {
		return formatMoney(limit.remainingAllowance, limit.currency);
	}

	return String(limit.remainingAllowance);
};

export const parseInputToApiValue = (type, rawValue) => {
	const raw = String(rawValue).trim();

	if (type === LIMIT_TYPES.SESSION_TIME) {
		const minutes = parseInt(raw, 10);

		if (Number.isNaN(minutes)) {
			return null;
		}

		const seconds = minutes * 60;
		return seconds >= SESSION_TIME_MIN_SECONDS ? seconds : null;
	}

	if (type === LIMIT_TYPES.DEPOSIT || type === LIMIT_TYPES.LOSS) {
		const parsed = parseFloat(raw);
		return parsed > 0 ? Math.round(parsed) : null;
	}

	const parsed = parseInt(raw, 10);
	return parsed > 0 ? parsed : null;
};

export const getInputValueFromLimit = (type, limit) => {
	if (!limit?.userMax) {
		return "";
	}

	if (type === LIMIT_TYPES.SESSION_TIME) {
		return String(Math.floor(Number(limit.userMax) / 60));
	}

	return String(limit.userMax);
};

export const getRgErrorMessage = (error, translate) => {
	const code = error?.data?.code || error?.code;
	const message = error?.data?.message || error?.message;

	const codeMap = {
		[RG_ERROR_CODES.INVALID_PARAM]: "limits.errors.invalidParam",
		[RG_ERROR_CODES.INVALID_LIMIT_TYPE]: "limits.errors.invalidLimitType",
		[RG_ERROR_CODES.INVALID_PERIOD]: "limits.errors.invalidPeriod",
		[RG_ERROR_CODES.EXCEEDS_BACKOFFICE_LIMIT]: "limits.errors.exceedsBackofficeLimit",
		[RG_ERROR_CODES.PENDING_LIMIT_CHANGE]: "limits.errors.pendingLimitChange",
	};

	if (code && codeMap[code]) {
		return translate(codeMap[code]);
	}

	return message || translate("limits.errors.generic");
};

export const isSessionTimeLimitError = (error) => {
	const status = error?.status || error?.error?.status || error?.response?.status;
	const code =
		error?.data?.code || error?.error?.data?.code || error?.response?.data?.code;

	return status === 403 && code === RG_ERROR_CODES.SESSION_TIME_LIMIT_EXCEEDED;
};

export const isDepositLimitError = (error) => {
	const code =
		error?.data?.code || error?.response?.data?.code || error?.code;

	return code === RG_ERROR_CODES.DEPOSIT_LIMIT_EXCEEDED;
};

export const isBetLimitError = (error) => {
	const code =
		error?.data?.code || error?.response?.data?.code || error?.code;

	return (
		code === RG_ERROR_CODES.LOSS_LIMIT_EXCEEDED ||
		code === RG_ERROR_CODES.BET_COUNT_LIMIT_EXCEEDED
	);
};

export const parseRgError = (error) => {
	const data = error?.data || error?.response?.data || error?.error?.data || {};

	return {
		code: data.code,
		message: data.message || data.Message,
		remainingAllowance: data.remainingAllowance,
		type: data.type,
		period: data.period,
		currency: data.currency,
	};
};

export const buildPeriodTableRows = (type, limits, translate) =>
	LIMIT_PERIOD_ORDER.map((period) => {
		const limit = findLimit(limits, type, period);

		return {
			key: `${type}-${period}`,
			period,
			limit,
			progressPercent: limit?.effectiveMax
				? getProgressPercent(limit.currentUsage, limit.effectiveMax)
				: 0,
			displayUsage: limit ? formatUsageValue(limit, translate) : "—",
			displayLimit: limit?.effectiveMax
				? formatLimitValue(limit, translate)
				: translate("limits.noLimitSet"),
			displayRemaining: limit ? formatRemainingValue(limit, translate) : "—",
			hasLimit: Boolean(limit?.effectiveMax || limit?.userMax),
		};
	});
